import { useEffect, useState } from 'react'
import { Button, Input, Modal, Space, message } from 'antd'
import { useTranslation } from 'react-i18next'

/**
 * Minimal "模板名称 + 模板简介" save dialog (spec 七) reused by "设为模板"
 * (Home.tsx). Deliberately reuses the *saving logic* the top template bar's
 * "新建模板" already relies on — `createAutoProcessTemplate` — rather than
 * duplicating that API call; this component only owns the name/description
 * form UI, which "设为模板" needs (no "初始参数来源" radio, since its params
 * always come from the source image's current effective config, never from
 * copying another template).
 */
export default function TemplateSaveDialog({
  open,
  title,
  initialName = '',
  onCancel,
  onSubmit,
}: {
  open: boolean
  title?: string
  initialName?: string
  onCancel: () => void
  onSubmit: (name: string, description: string) => Promise<void>
}) {
  const { t } = useTranslation(['autoProcess', 'actions'])
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setName(initialName)
      setDescription('')
    }
  }, [open, initialName])

  const handleSubmit = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      message.warning(t('templateSave.nameRequired'))
      return
    }
    setSaving(true)
    try {
      await onSubmit(trimmed, description.trim())
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={title ?? t('templateSave.title')}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      data-testid="template-save-dialog"
    >
      <div className="apt-create-form">
        <label className="apt-field">
          <span>{t('templateSave.nameLabel')}</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('templateSave.namePlaceholder')}
            autoFocus
            onPressEnter={handleSubmit}
            data-testid="template-save-name-input"
          />
        </label>
        <label className="apt-field">
          <span>{t('templateSave.descriptionLabel')}</span>
          <Input.TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('templateSave.descriptionPlaceholder')}
            rows={2}
            data-testid="template-save-description-input"
          />
        </label>
        <Space style={{ marginTop: 8 }}>
          <Button onClick={onCancel} disabled={saving}>
            {t('actions:cancel')}
          </Button>
          <Button type="primary" loading={saving} onClick={handleSubmit} data-testid="template-save-confirm">
            {t('actions:save')}
          </Button>
        </Space>
      </div>
    </Modal>
  )
}
